// src/components/profile/AchievementModal.tsx
// Modal untuk menampilkan detail prestasi

import React, { useState } from "react";
import { X, Trophy, Medal, Award, Star, Calendar, MapPin, Share2 } from "lucide-react";
import { PhotoView } from "react-photo-view";
import SharePopup from "../gallery/SharePopup";
import { useOptimizedProfile } from "../../hooks/useOptimizedProfile";
import { useSocialLinks } from "../../hooks/useSocialLinks";

interface Achievement {
    id: string;
    title: string;
    description: string;
    level: string;
    category: string;
    image_url?: string;
    date: string;
    achievement_date: string;
}

interface AchievementModalProps {
    achievement: Achievement | null;
    isOpen: boolean;
    onClose: () => void;
}

const AchievementModal: React.FC<AchievementModalProps> = ({
    achievement,
    isOpen,
    onClose,
}) => {
    const [isShareOpen, setIsShareOpen] = useState(false);
    const { profileData } = useOptimizedProfile();
    const { socialLinks } = useSocialLinks();

    if (!isOpen || !achievement) return null;

    const instagramHandle = socialLinks?.instagram
        ? '@' + socialLinks.instagram.replace(/^https?:\/\/(www\.)?instagram\.com\//i, '').replace(/\/$/, '')
        : '@evano.rama';

    // Fungsi untuk mendapatkan icon berdasarkan level prestasi
    const getAchievementIcon = (level: string) => {
        switch (level) {
            case "Nasional":
                return <Trophy className="h-8 w-8 text-yellow-500" />;
            case "Provinsi":
                return <Medal className="h-8 w-8 text-silver-500" />;
            case "Kota":
            case "Kabupaten":
                return <Award className="h-8 w-8 text-orange-500" />;
            case "Sekolah":
                return <Star className="h-8 w-8 text-blue-500" />;
            case "Event Organizer":
                return <Star className="h-8 w-8 text-purple-500" />;
            default:
                return <Trophy className="h-8 w-8 text-gray-500" />;
        }
    };

    // Fungsi untuk mendapatkan warna badge berdasarkan level
    const getBadgeColor = (level: string) => {
        switch (level) {
            case "Nasional":
                return "bg-yellow-100 text-yellow-800";
            case "Provinsi":
                return "bg-gray-100 text-gray-800";
            case "Kota":
            case "Kabupaten":
                return "bg-orange-100 text-orange-800";
            case "Sekolah":
                return "bg-blue-100 text-blue-800";
            case "Event Organizer":
                return "bg-purple-100 text-purple-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    // Fungsi untuk mendapatkan warna berdasarkan kategori
    const getCategoryColor = (category: string) => {
        if (!category) {
            return "bg-gray-100 text-gray-800";
        }

        switch (category.toLowerCase()) {
            case "event organizer":
                return "bg-purple-100 text-purple-800";
            case "akademik":
                return "bg-blue-100 text-blue-800";
            case "olahraga":
                return "bg-green-100 text-green-800";
            case "seni":
                return "bg-pink-100 text-pink-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal Content */}
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="p-2 bg-gray-100 rounded-full">
                                {getAchievementIcon(achievement.level)}
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">
                                    Detail Prestasi
                                </h2>
                                <p className="text-sm text-gray-500">
                                    {achievement.category}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-1.5">
                            {achievement.image_url && (
                                <button
                                    onClick={() => setIsShareOpen(true)}
                                    className="p-2 hover:bg-pink-50 rounded-full transition-colors text-gray-500 hover:text-pink-600"
                                    title="Bagikan Prestasi"
                                >
                                    <Share2 className="h-5 w-5" />
                                </button>
                            )}
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <X className="h-5 w-5 text-gray-500" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Image */}
                    {achievement.image_url && (
                        <div className="mb-6">
                            <PhotoView src={achievement.image_url}>
                                <img
                                    src={achievement.image_url}
                                    alt={achievement.title}
                                    className="w-full h-64 object-cover rounded-xl shadow-lg cursor-pointer hover:opacity-90 transition-opacity"
                                    onError={(e) => {
                                        const target =
                                            e.target as HTMLImageElement;
                                        target.style.display = "none";
                                    }}
                                />
                            </PhotoView>
                            <p className="text-xs text-gray-500 mt-2 text-center">
                                Klik gambar untuk memperbesar
                            </p>
                        </div>
                    )}

                    {/* Title */}
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">
                        {achievement.title}
                    </h1>

                    {/* Badges */}
                    <div className="flex flex-wrap gap-2 mb-6">
                        {/* Level Badge */}
                        <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getBadgeColor(
                                achievement.level
                            )}`}
                        >
                            <MapPin className="h-4 w-4 mr-1" />
                            {achievement.level}
                        </span>

                        {/* Category Badge */}
                        <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(
                                achievement.category
                            )}`}
                        >
                            {achievement.category}
                        </span>

                        {/* Date Badge */}
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                            <Calendar className="h-4 w-4 mr-1" />
                            {new Date(
                                achievement.date || achievement.achievement_date
                            ).toLocaleDateString("id-ID", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                            })}
                        </span>
                    </div>

                    {/* Description */}
                    <div className="bg-gray-50 rounded-xl p-6 mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">
                            Deskripsi
                        </h3>
                        <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                            {achievement.description ||
                                "Tidak ada deskripsi tersedia."}
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 rounded-b-2xl flex items-center justify-between">
                    {achievement.image_url ? (
                        <button
                            onClick={() => setIsShareOpen(true)}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-600 hover:opacity-95 text-white text-sm font-semibold rounded-xl shadow-xs transition-all active:scale-95 cursor-pointer"
                        >
                            <Share2 className="h-4 w-4" />
                            <span>Bagikan Prestasi</span>
                        </button>
                    ) : <div />}
                    <button
                        onClick={onClose}
                        className="px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors cursor-pointer"
                    >
                        Tutup
                    </button>
                </div>
            </div>

            {/* Share Popup */}
            {achievement.image_url && (
                <SharePopup
                    isOpen={isShareOpen}
                    onClose={() => setIsShareOpen(false)}
                    imageUrl={achievement.image_url}
                    imageTitle={achievement.title}
                    imageDescription={achievement.description || `Juara / Prestasi Tingkat ${achievement.level}`}
                    authorName={profileData?.fullName || 'Evano Rama'}
                    authorAvatar={profileData?.profileImageUrl}
                    authorHandle={instagramHandle}
                />
            )}
        </div>
    );
};

export default AchievementModal;
